import { Component, type ErrorInfo, type PropsWithChildren, type ReactNode } from "react";
import { type DisplayableError } from "./DisplayableError";
import { type IErrorContext } from "./ErrorContext";

export type FallbackUI = ({ error }: { error: DisplayableError | Error }) => JSX.Element;

export interface ErrorBoundaryWithTrackingProps extends PropsWithChildren {
    fallbackUI: FallbackUI;
    asyncErrorContext: IErrorContext;
    trackException?: (error: Error | DisplayableError, errorInfo?: ErrorInfo) => void;
}

export interface ErrorBoundaryWithTrackingState {
    error: Error | DisplayableError | null;
}

export class ErrorBoundaryWithTracking extends Component<
    ErrorBoundaryWithTrackingProps,
    ErrorBoundaryWithTrackingState
> {
    constructor(props: ErrorBoundaryWithTrackingProps) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error: Error | DisplayableError): ErrorBoundaryWithTrackingState {
        return { error };
    }

    componentDidCatch(error: Error | DisplayableError, errorInfo: ErrorInfo) {
        this.props.trackException?.(error, errorInfo);
    }

    render(): ReactNode {
        const error = this.props.asyncErrorContext.error ?? this.state.error;
        if (error !== null) {
            const { fallbackUI: FallbackUI } = this.props;
            return <FallbackUI error={error} />;
        }
        return this.props.children;
    }
}
